import { Brain, BarChart3, Sailboat, Bot, Home, Trophy, ReceiptText, type LucideIcon } from "lucide-react"

/**
 * Certifications actually held, as opposed to the ones still in progress.
 *
 * This replaced a "6+ Years in Tech" figure on the homepage. That number was
 * counting the DigiPen teaching assistantship and freelance work alongside a
 * degree finished in 2023, so it set an expectation — six years of full-time
 * delivery — that LinkedIn immediately contradicted with a first full-time
 * role in Mar 2023. A certification count has the opposite property: it is
 * independently checkable, and it understates rather than overstates.
 *
 * Derived rather than hard-coded so it follows the list below; the two
 * Databricks entries move from "in progress" to held by editing their `date`.
 */
export const IN_PROGRESS_PREFIX = 'In Progress'

export function heldCertifications() {
  return certifications.filter((c) => !c.date?.startsWith(IN_PROGRESS_PREFIX))
}

export function inProgressCertifications() {
  return certifications.filter((c) => c.date?.startsWith(IN_PROGRESS_PREFIX))
}

/**
 * Career facts for the places that state them in prose — the two chatbots and
 * the LLM system prompt.
 *
 * Those three each carried their own copy of the role titles and dates, and
 * all three were wrong at the same time: they claimed "6+ years of experience"
 * and "Deloitte Risk Advisory" long after the timeline here said otherwise. A
 * visitor asking the assistant about his background got confidently-stated
 * stale facts, which is the worst surface to be wrong on.
 *
 * Deriving them means a job change is edited once, in resumeItems below.
 */
export function currentRole() {
  return resumeItems[0]
}

/** e.g. "Regional Technical Consultant, Portfolio Pre-Sales at NiCE Actimize" */
export function currentRoleLine() {
  const r = currentRole()
  return `${r.role} at ${r.company}`
}

/**
 * The professional roles, most recent first, as a single sentence.
 *
 * `limit` defaults to the three full-time positions; the teaching
 * assistantship and freelance work sit below those in resumeItems and are
 * context rather than career history.
 */
export function careerSummary(limit = 3) {
  const [current, ...rest] = resumeItems.slice(0, limit)
  const previous = rest.map((r) => `${r.role} at ${r.company} (${r.duration})`).join(', ')
  return (
    `${current.role} at ${current.company} (${current.duration})` +
    (previous ? `. Previously ${previous}` : '')
  )
}

export interface Project {
  slug: string
  title: string
  description: string
  tech: string[]
  // "retired": shipped as its own product, then wound down. Its old domain
  // redirects to the project page, so the page has to explain itself.
  status: "live" | "in-development" | "abandoned" | "retired"
  liveUrl?: string
  codeLink?: string
  icon: LucideIcon
  /** Longer account shown on the project page, one string per paragraph. */
  story?: string[]
}

export const projects: Project[] = [
  {
    slug: "world-cup-prediction",
    title: "World Cup 2026 Prediction Platform",
    description:
      "End-to-end data pipeline: Python scraper covering 1,248 players across 48 squads, API-Football integration, position-aware per-match rating engine, and an always-on match collector — feeding a Next.js 15 + Supabase prediction site using Dixon-Coles/Poisson modelling.",
    tech: ["Python", "Next.js 15", "Supabase", "API-Football", "Dixon-Coles", "Data Pipelines"],
    status: "live",
    liveUrl: "https://wc2026-analyst.vercel.app",
    icon: Trophy,
  },
  {
    slug: "workinvoice",
    title: "WorkInvoice",
    description:
      "Flat-fee invoicing for Singapore freelancers: SGQR-compliant PayNow QR codes with the amount locked in, GST tax-invoice layout, and free-plan limits enforced in Postgres. Built and launched solo, then retired.",
    tech: ["Next.js 16", "React 19", "Supabase", "Postgres RLS", "Tailwind CSS", "PayNow / SGQR", "Vercel"],
    status: "retired",
    icon: ReceiptText,
    story: [
      "WorkInvoice was a flat SGD 5/month invoicing tool for Singapore freelancers whose clients pay by PayNow or bank transfer — the pitch was keeping 100% of every invoice instead of losing 3–4% to card processors.",
      "I built and ran it end to end: an SGQR PayNow encoder with its checksum verified independently, GST tax invoices with the fields IRAS expects, printable PDFs, instant sign-up with optional passwords, and plan limits and upgrade rules enforced by Postgres triggers rather than trusted to the client.",
      "I retired it in October 2026. Freelancers who aren't GST-registered already get by with free tools, and Singapore's InvoiceNow programme gives small businesses free e-invoicing software until 2031 — so a paid tool was competing with free on both sides. workinvoice.app now points here.",
    ],
  },
  {
    slug: "grant-advisor",
    title: "Grant Advisor - RAG AI Chatbot",
    description: "Intelligent chatbot that communicates with users to recommend suitable grants based on conversational context and requirements.",
    tech: ["LangChain", "Vector DB", "RAG", "OpenAI API", "Node.js"],
    status: "in-development",
    icon: Bot,
  },
  {
    slug: "sailpoint-rule-generator",
    title: "SailPoint Automation Rule Generator",
    description: "Internal tool that significantly cut down debugging time for SailPoint IIQ rules by automating generation and validation.",
    tech: ["Java", "XML", "SailPoint IIQ", "Groovy"],
    status: "abandoned",
    icon: Sailboat,
  },
  {
    slug: "newhome-sg",
    title: "NewHome SG",
    description: "Property and real-estate experience with 3D visualization, AI assistants, and document handling. Next.js with Three.js and Konva.",
    tech: ["Next.js", "Three.js", "Firebase", "Anthropic AI", "Google Gemini", "PDF.js"],
    status: "live",
    liveUrl: "https://newhome-sg.vercel.app",
    icon: Home,
  },
  {
    slug: "personal-finance-analyst",
    title: "Personal Finance Analyst",
    description: "AI-powered personal finance dashboard with analytics, budgeting insights, and visualizations. Built with Next.js and Neon DB.",
    tech: ["Next.js", "Neon DB", "Recharts", "React Hook Form", "Zod"],
    status: "in-development",
    icon: BarChart3,
  },
  {
    slug: "tune",
    title: "TUNE — AI Development Platform",
    description: "A collaborative platform for SMEs to fine-tune, develop, and deploy LLMs easily. Simplifying complex AI workflows for teams.",
    tech: ["Next.js", "Python", "Docker", "OpenAI API", "Vector DB"],
    status: "in-development",
    icon: Brain,
  },
]

export const resumeItems = [
  {
    logoUrl: "/placeholder.svg?width=50&height=50",
    company: "NiCE Actimize",
    role: "Regional Technical Consultant, Portfolio Pre-Sales",
    duration: "Sep 2025 - Present",
    description:
      "Partnering with financial institutions across APAC to design and demonstrate Actimize-based financial crime, fraud, and compliance solutions. Translating complex regulatory and risk requirements into clear technical architectures and value-driven demos.",
    tags: ["FinTech", "Pre-Sales", "Actimize", "APAC", "Financial Crime"],
  },
  {
    logoUrl: "/placeholder.svg?width=50&height=50",
    company: "Deloitte",
    role: "Solution Engineer",
    duration: "May 2024 - Sep 2025",
    description:
      "Leading enterprise IAM implementations, cybersecurity strategies, and digital transformation projects for Fortune 500 clients. Specializing in SailPoint IIQ and identity governance solutions.",
    tags: ["IAM", "SailPoint", "Cybersecurity", "Consulting", "Enterprise"],
  },
  {
    logoUrl: "/placeholder.svg?width=50&height=50",
    company: "Doerscircle",
    // Started as a contract Software Engineer in Mar 2023 and converted;
    // LinkedIn shows both titles under one 1 yr 3 mos tenure.
    role: "Solutions Architect",
    duration: "Mar 2023 - May 2024",
    description:
      "Led legacy code refactoring from Vue 2 to Vue 3, reducing complexity by 30% and accelerating feature delivery by 25%. Integrated Google Analytics to enhance user behavior tracking, resulting in 25% increased user retention.",
    tags: ["Full-Stack", "Vue.js", "Analytics", "Startup"],
  },
  {
    logoUrl: "/placeholder.svg?width=50&height=50",
    company: "DigiPen Institute of Technology Singapore",
    role: "Teacher's Assistant",
    duration: "2019 - 2023",
    description:
      "Assisted in Maths, Computer Science, and English modules. Developed three industry-standard games in C and C++ employing AI, ML, and custom-built game engines. Increased student engagement by 20%.",
    tags: ["Education", "Game Development", "C++", "Mentoring"],
  },
  {
    logoUrl: "/placeholder.svg?width=50&height=50",
    company: "Freelance",
    role: "Web Developer",
    duration: "2020 - 2023",
    description:
      "Developed websites for small businesses using Docker, Django, HTML, and web hosting, achieving a 20% boost in online traffic and 15% increase in customer engagement. Led 5 projects from concept to completion.",
    tags: ["Web Development", "Django", "Docker", "Freelance"],
  },
]

export const certifications = [
  { name: "AWS Certified Solutions Architect – Associate", issuer: "Amazon Web Services", date: "Nov 2024 – Nov 2027" },
  { name: "Databricks Certified Data Engineer Associate", issuer: "Databricks", date: "In Progress · Target Sep 2026" },
  { name: "Databricks Certified Generative AI Engineer Associate", issuer: "Databricks", date: "In Progress · Target Oct 2026" },
  { name: "Neo4j Certified Professional", issuer: "Neo4j", date: "Aug 2024" },
  { name: "Neo4j Graph Data Science Certification", issuer: "Neo4j", date: "Aug 2024" },
  { name: "SailPoint Certified Identity Security Engineer", issuer: "SailPoint", date: "May 2025 – May 2027" },
  { name: "Fundamentals of MCP", issuer: "Hugging Face", date: "Jul 2025" },
  { name: "Professional Scrum Master™ I (PSM I)", issuer: "Scrum.org", date: "Aug 2024" },
  { name: "IFM-X Implementation Certification", issuer: "NiCE Actimize", date: "2025" },
  { name: "SAM Implementation Certification", issuer: "NiCE Actimize", date: "2025" },
  { name: "CDD Implementation Certification", issuer: "NiCE Actimize", date: "2025" },
]

export const skills = {
  languagesFrameworks: ["Python", "TypeScript/JavaScript", "React", "Next.js", "Node.js", "SQL"],
  dataAi: ["RAG & LLM Applications", "Neo4j / Cypher", "Graph Data Science", "Supabase / Postgres", "Data Pipelines"],
  cloudPlatforms: ["AWS", "Vercel", "NICE Actimize (IFM-X, SAM, CDD)", "SailPoint"],
  domain: ["Fraud Detection", "AML / KYC", "Identity & Access Management", "Presales & POC Delivery"],
  currentlyLearning: ["Databricks Data Engineer Associate", "Databricks Generative AI Engineer Associate"],
}

// Links point at the on-site posts, not their Medium copies. The Medium
// versions self-canonicalise to Medium, so linking there sent both readers and
// link equity to a higher-authority domain competing for the same queries.
// The hand-maintained `articles` array that used to live here is gone. It
// duplicated posts/*.mdx and had already drifted — the homepage advertised
// five essays while the blog served six. The writing section now receives
// the real posts from getAllPosts() via app/page.tsx.
